/**
 * Losi OpenUI Library
 *
 * Component library the model may emit via OpenUI Lang in chat.
 *
 * Primary responsibilities:
 * - Define Losi-styled generative UI primitives with Zod prop schemas
 * - Support Query()/Mutation()-driven dashboards via reactive forms
 * - Keep actions safe (triggerAction → chat follow-up, no raw DOM handlers)
 *
 * Dependencies:
 * - @openuidev/react-lang
 * - zod/v4
 * - Losi UI button
 */

'use client'

import { useContext } from 'react'
import {
  createLibrary,
  defineComponent,
  reactive,
  useStateField,
  useTriggerAction
} from '@openuidev/react-lang'
import { z } from 'zod/v4'

import { OpenUIContactsPanel } from '@/components/openui/openui-contacts-panel'
import { OpenUINotesBrowser } from '@/components/openui/openui-notes-browser'
import { OpenUITaskViews } from '@/components/openui/openui-task-views'
import { Button } from '@/components/ui/button'
import { ChatbotUIContext } from '@/context/context'
import {
  openUIMetricClass,
  openUIPanelClass,
  openUIRowClass,
  openUISubtitleClass,
  openUITitleClass
} from '@/lib/openui/chrome'
import { dispatchSidekickOpenSpaceItem } from '@/lib/sidekick/space-item-context'
import { cn } from '@/lib/utils'

const openUIChildren = z
  .array(z.any())
  .describe('Child OpenUI components')

const Text = defineComponent({
  name: 'Text',
  description: 'Body or muted text block.',
  props: z.object({
    content: z.string().describe('Text content'),
    tone: z.enum(['default', 'muted']).optional().describe('Visual tone')
  }),
  component: ({ props }) => (
    <p
      className={cn(
        'text-sm leading-6',
        props.tone === 'muted' ? 'text-muted-foreground' : 'text-foreground'
      )}
    >
      {props.content}
    </p>
  )
})

const Heading = defineComponent({
  name: 'Heading',
  description: 'Section heading for generated UI.',
  props: z.object({
    title: z.string().describe('Heading title'),
    subtitle: z.string().optional().describe('Optional subtitle')
  }),
  component: ({ props }) => (
    <div className="space-y-0.5 px-1">
      <h3 className="text-[15px] font-semibold tracking-[-0.02em] text-foreground">
        {props.title}
      </h3>
      {props.subtitle ? (
        <p className={openUISubtitleClass}>{props.subtitle}</p>
      ) : null}
    </div>
  )
})

const Metric = defineComponent({
  name: 'Metric',
  description: 'Single KPI metric with optional hint. Prefer Query()-backed values.',
  props: z.object({
    label: z.string().describe('Metric label'),
    value: z.union([z.string(), z.number()]).describe('Metric value'),
    hint: z.string().optional().describe('Optional helper text')
  }),
  component: ({ props }) => (
    <div className={openUIMetricClass}>
      <p className="text-[11px] font-medium tracking-[0.02em] text-muted-foreground">
        {props.label}
      </p>
      <p className="mt-0.5 text-[22px] font-semibold tracking-[-0.03em] text-foreground tabular-nums">
        {props.value}
      </p>
      {props.hint ? (
        <p className={cn('mt-0.5', openUISubtitleClass)}>{props.hint}</p>
      ) : null}
    </div>
  )
})

const StatusBadge = defineComponent({
  name: 'StatusBadge',
  description: 'Small status pill.',
  props: z.object({
    label: z.string(),
    tone: z.enum(['default', 'success', 'warning', 'danger']).optional()
  }),
  component: ({ props }) => (
    <span
      className={cn(
        'inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-medium',
        props.tone === 'success' && 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
        props.tone === 'warning' && 'bg-amber-500/15 text-amber-800 dark:text-amber-200',
        props.tone === 'danger' && 'bg-red-500/15 text-red-700 dark:text-red-300',
        (!props.tone || props.tone === 'default') &&
          'bg-muted text-muted-foreground'
      )}
    >
      {props.label}
    </span>
  )
})

const ActionButton = defineComponent({
  name: 'ActionButton',
  description:
    'Button that sends a follow-up chat message (safe action; no arbitrary JS).',
  props: z.object({
    label: z.string().describe('Button label'),
    prompt: z.string().describe('User message to send when clicked'),
    variant: z
      .enum(['default', 'secondary', 'outline'])
      .optional()
      .describe('Button style')
  }),
  component: function OpenUIActionButton({ props }) {
    const triggerAction = useTriggerAction()
    return (
      <Button
        type="button"
        size="sm"
        variant={props.variant || 'default'}
        onClick={() => triggerAction(props.prompt)}
      >
        {props.label}
      </Button>
    )
  }
})

const FollowUpBlock = defineComponent({
  name: 'FollowUpBlock',
  description: 'Row of follow-up suggestion chips that continue the chat.',
  props: z.object({
    prompts: z.array(z.string()).describe('Follow-up prompts')
  }),
  component: function OpenUIFollowUpBlock({ props }) {
    const triggerAction = useTriggerAction()
    return (
      <div className="flex flex-wrap gap-2">
        {props.prompts.map(prompt => (
          <Button
            key={prompt}
            type="button"
            size="sm"
            variant="outline"
            onClick={() => triggerAction(prompt)}
          >
            {prompt}
          </Button>
        ))}
      </div>
    )
  }
})

const TaskCard = defineComponent({
  name: 'TaskCard',
  description:
    'Compact Spaces task summary. Pass taskId (+ optional spaceId) so click opens the real task modal; otherwise use actionPrompt for a chat follow-up. Prefer TaskViews for boards/lists.',
  props: z.object({
    title: z.string().describe('Task title'),
    status: z.string().optional().describe('Task status'),
    due: z.string().optional().describe('Due date label'),
    summary: z.string().optional().describe('Short summary'),
    taskId: z
      .string()
      .optional()
      .describe('Space task id — click opens the Spaces task modal'),
    spaceId: z
      .string()
      .optional()
      .describe('Space id (optional when a Space is already selected)'),
    actionPrompt: z
      .string()
      .optional()
      .describe('Follow-up chat message when Continue is clicked')
  }),
  component: function OpenUITaskCard({ props }) {
    const triggerAction = useTriggerAction()
    const { selectedSpace } = useContext(ChatbotUIContext)
    const openTask = () => {
      if (!props.taskId) return
      const spaceId = (props.spaceId || selectedSpace?.id || '').trim()
      if (!spaceId) return
      dispatchSidekickOpenSpaceItem({
        spaceId,
        type: 'task',
        id: props.taskId,
        title: props.title,
        summary: props.summary || null
      })
    }
    return (
      <article
        className={cn(
          openUIRowClass,
          'flex-col items-stretch',
          props.taskId && 'cursor-pointer'
        )}
        onClick={props.taskId ? openTask : undefined}
        onKeyDown={
          props.taskId
            ? event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  openTask()
                }
              }
            : undefined
        }
        role={props.taskId ? 'button' : undefined}
        tabIndex={props.taskId ? 0 : undefined}
      >
        <div className="flex items-start justify-between gap-3">
          <h4 className={openUITitleClass}>{props.title}</h4>
          {props.status ? (
            <span className="text-[11px] font-medium text-muted-foreground">
              {props.status}
            </span>
          ) : null}
        </div>
        {props.due ? (
          <p className={cn('mt-0.5', openUISubtitleClass)}>Due {props.due}</p>
        ) : null}
        {props.summary ? (
          <p className={cn('mt-1 line-clamp-2', openUISubtitleClass)}>
            {props.summary}
          </p>
        ) : null}
        {props.actionPrompt ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="mt-1 h-7 self-start px-2 text-xs"
            onClick={event => {
              event.stopPropagation()
              triggerAction(props.actionPrompt!)
            }}
          >
            Continue
          </Button>
        ) : null}
      </article>
    )
  }
})

const TaskViews = defineComponent({
  name: 'TaskViews',
  description:
    'PREFERRED for Space tasks. Live interactive task surface with the same views Spaces supports (board/kanban, list, table, timeline, calendar, gantt). Drag on the board updates status for real; click opens the Spaces task modal. Omit spaceId to use the currently selected Space.',
  props: z.object({
    title: z.string().optional().describe('Panel title'),
    spaceId: z
      .union([z.string(), z.null()])
      .optional()
      .describe('Space id (optional when a Space is selected; null = current)'),
    defaultView: z
      .enum(['board', 'list', 'table', 'timeline', 'calendar', 'gantt'])
      .optional()
      .describe('Initial view — default board (kanban)')
  }),
  component: function OpenUITaskViewsComponent({ props }) {
    return (
      <OpenUITaskViews
        title={props.title}
        spaceId={props.spaceId}
        defaultView={props.defaultView || 'board'}
      />
    )
  }
})

const NotesBrowser = defineComponent({
  name: 'NotesBrowser',
  description:
    'Live Space notes list. Click a row to open the real note modal. Omit spaceId to use the selected Space.',
  props: z.object({
    title: z.string().optional(),
    spaceId: z.union([z.string(), z.null()]).optional(),
    limit: z.number().optional()
  }),
  component: function OpenUINotesBrowserComponent({ props }) {
    return (
      <OpenUINotesBrowser
        title={props.title}
        spaceId={props.spaceId}
        limit={props.limit}
      />
    )
  }
})

const ContactsPanel = defineComponent({
  name: 'ContactsPanel',
  description:
    'Live Nexus CRM contacts panel. Click a contact to continue in chat with context. Omit workspaceId to use the selected workspace.',
  props: z.object({
    title: z.string().optional(),
    workspaceId: z.union([z.string(), z.null()]).optional(),
    limit: z.number().optional()
  }),
  component: function OpenUIContactsPanelComponent({ props }) {
    return (
      <OpenUIContactsPanel
        title={props.title}
        workspaceId={props.workspaceId}
        limit={props.limit}
      />
    )
  }
})

const ContactCard = defineComponent({
  name: 'ContactCard',
  description: 'Compact CRM contact / lead card.',
  props: z.object({
    name: z.string().describe('Contact name'),
    company: z.string().optional().describe('Company name'),
    email: z.string().optional().describe('Email'),
    status: z.string().optional().describe('Pipeline status'),
    actionPrompt: z
      .string()
      .optional()
      .describe('Follow-up chat message when Open in CRM is clicked')
  }),
  component: function OpenUIContactCard({ props }) {
    const triggerAction = useTriggerAction()
    return (
      <article className={cn(openUIRowClass, 'flex-col items-stretch')}>
        <h4 className={openUITitleClass}>{props.name}</h4>
        {props.company ? (
          <p className={cn('mt-0.5', openUISubtitleClass)}>{props.company}</p>
        ) : null}
        <div className={cn('mt-1 flex flex-wrap gap-2', openUISubtitleClass)}>
          {props.email ? <span>{props.email}</span> : null}
          {props.status ? <span>{props.status}</span> : null}
        </div>
        {props.actionPrompt ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="mt-1 h-7 self-start px-2 text-xs"
            onClick={() => triggerAction(props.actionPrompt!)}
          >
            Open in CRM
          </Button>
        ) : null}
      </article>
    )
  }
})

const ApprovalCard = defineComponent({
  name: 'ApprovalCard',
  description: 'Ask the user to approve or reject a proposed action via chat.',
  props: z.object({
    title: z.string().describe('Approval title'),
    summary: z.string().describe('What the user is approving'),
    approvePrompt: z.string().describe('Message sent when Allow is clicked'),
    rejectPrompt: z
      .string()
      .optional()
      .describe('Message sent when Deny is clicked')
  }),
  component: function OpenUIApprovalCard({ props }) {
    const triggerAction = useTriggerAction()
    return (
      <article className="flex flex-col gap-2 border-y border-amber-500/20 py-3">
        <h4 className={openUITitleClass}>{props.title}</h4>
        <p className={cn('leading-5', openUISubtitleClass)}>{props.summary}</p>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button type="button" size="sm" onClick={() => triggerAction(props.approvePrompt)}>
            Allow
          </Button>
          {props.rejectPrompt ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => triggerAction(props.rejectPrompt!)}
            >
              Deny
            </Button>
          ) : null}
        </div>
      </article>
    )
  }
})

const BarChart = defineComponent({
  name: 'BarChart',
  description: 'Simple horizontal bar chart from label/value pairs.',
  props: z.object({
    title: z.string().optional().describe('Chart title'),
    items: z
      .array(
        z.object({
          label: z.string(),
          value: z.number()
        })
      )
      .describe('Bar items')
  }),
  component: ({ props }) => {
    const max = Math.max(...props.items.map(item => item.value), 1)
    return (
      <section className={openUIPanelClass}>
        {props.title ? <h4 className={openUITitleClass}>{props.title}</h4> : null}
        <div className="space-y-2.5 px-1">
          {props.items.map(item => (
            <div key={item.label} className="space-y-1">
              <div className="flex items-center justify-between text-[12px] text-muted-foreground">
                <span>{item.label}</span>
                <span className="tabular-nums text-foreground/80">{item.value}</span>
              </div>
              <div className="h-1 overflow-hidden rounded-full bg-foreground/[0.06]">
                <div
                  className="h-full origin-left rounded-full bg-foreground/55 motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-out"
                  style={{
                    transform: `scaleX(${Math.max(0.04, item.value / max)})`
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>
    )
  }
})

const DataList = defineComponent({
  name: 'DataList',
  description:
    'List of title/subtitle rows — ideal for Query() results (tasks.rows, contacts.rows).',
  props: z.object({
    title: z.string().optional(),
    items: z
      .array(
        z.object({
          title: z.string(),
          subtitle: z.string().optional(),
          meta: z.string().optional()
        })
      )
      .describe('List rows')
  }),
  component: ({ props }) => (
    <section className={openUIPanelClass}>
      {props.title ? (
        <h4 className={cn('px-1', openUITitleClass)}>{props.title}</h4>
      ) : null}
      <ul>
        {props.items.map((item, index) => (
          <li key={`${item.title}-${index}`} className={openUIRowClass}>
            <div className="min-w-0">
              <p className="truncate text-[13px] font-medium text-foreground">
                {item.title}
              </p>
              {item.subtitle ? (
                <p className={cn('mt-0.5 truncate', openUISubtitleClass)}>
                  {item.subtitle}
                </p>
              ) : null}
            </div>
            {item.meta ? (
              <span className="shrink-0 text-[11px] text-muted-foreground">
                {item.meta}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  )
})

const SelectItem = defineComponent({
  name: 'SelectItem',
  description: 'Option for Select.',
  props: z.object({
    value: z.string(),
    label: z.string()
  }),
  component: ({ props }) => (
    <option value={props.value}>{props.label}</option>
  )
})

const Input = defineComponent({
  name: 'Input',
  description: 'Text input bound to a $variable via reactive value.',
  props: z.object({
    name: z.string().describe('Field name'),
    value: reactive(z.string().optional()).describe('Bound $variable'),
    placeholder: z.string().optional()
  }),
  component: function OpenUIInput({ props }) {
    const field = useStateField(props.name, props.value)
    return (
      <input
        name={props.name}
        value={typeof field.value === 'string' ? field.value : ''}
        placeholder={props.placeholder}
        onChange={event => field.setValue(event.target.value)}
        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
      />
    )
  }
})

const Select = defineComponent({
  name: 'Select',
  description: 'Select bound to a $variable.',
  props: z.object({
    name: z.string(),
    value: reactive(z.string().optional()),
    items: z.array(SelectItem.ref)
  }),
  component: function OpenUISelect({ props, renderNode }) {
    const field = useStateField(props.name, props.value)
    return (
      <select
        name={props.name}
        value={typeof field.value === 'string' ? field.value : ''}
        onChange={event => field.setValue(event.target.value)}
        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
      >
        {renderNode(props.items)}
      </select>
    )
  }
})

const FormControl = defineComponent({
  name: 'FormControl',
  description: 'Labeled form field wrapper.',
  props: z.object({
    label: z.string(),
    children: openUIChildren
  }),
  component: ({ props, renderNode }) => (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{props.label}</span>
      {renderNode(props.children)}
    </label>
  )
})

const SubmitButton = defineComponent({
  name: 'SubmitButton',
  description:
    'Primary form submit that continues the conversation with the button label (pair with Mutation + @Run in Action when using tools).',
  props: z.object({
    label: z.string(),
    prompt: z
      .string()
      .optional()
      .describe('Optional override message; defaults to label')
  }),
  component: function OpenUISubmitButton({ props }) {
    const triggerAction = useTriggerAction()
    return (
      <Button
        type="button"
        size="sm"
        onClick={() => triggerAction(props.prompt || props.label)}
      >
        {props.label}
      </Button>
    )
  }
})

const Form = defineComponent({
  name: 'Form',
  description: 'Form container for create/edit flows.',
  props: z.object({
    name: z.string().describe('Form name for state scoping'),
    children: openUIChildren
  }),
  component: ({ props, renderNode }) => (
    <form
      name={props.name}
      className="flex flex-col gap-3 py-1"
      onSubmit={event => event.preventDefault()}
    >
      {renderNode(props.children)}
    </form>
  )
})

const Card = defineComponent({
  name: 'Card',
  description:
    'Named section for related children. Flat — no nested box chrome. Prefer Stack + Heading; avoid wrapping TaskViews/NotesBrowser/ContactsPanel.',
  props: z.object({
    title: z.string().optional().describe('Section title'),
    children: openUIChildren.optional().describe('Child components')
  }),
  component: ({ props, renderNode }) => (
    <section className={cn(openUIPanelClass, 'py-1')}>
      {props.title ? <h4 className={openUITitleClass}>{props.title}</h4> : null}
      <div className="flex flex-col gap-2">{renderNode(props.children)}</div>
    </section>
  )
})

const Stack = defineComponent({
  name: 'Stack',
  description: 'Vertical or horizontal layout container for OpenUI children.',
  props: z.object({
    children: openUIChildren.describe('Child components'),
    direction: z
      .enum(['column', 'row'])
      .optional()
      .describe('Layout direction'),
    gap: z.enum(['s', 'm', 'l']).optional().describe('Spacing between children')
  }),
  component: ({ props, renderNode }) => {
    const gapClass =
      props.gap === 's' ? 'gap-2' : props.gap === 'l' ? 'gap-5' : 'gap-3'
    return (
      <div
        className={cn(
          'flex w-full',
          props.direction === 'row' ? 'flex-row flex-wrap items-start' : 'flex-col',
          gapClass
        )}
      >
        {renderNode(props.children)}
      </div>
    )
  }
})

export const losiOpenUILibrary = createLibrary({
  root: 'Stack',
  componentGroups: [
    {
      name: 'Layout',
      components: ['Stack', 'Card', 'Heading', 'Text', 'StatusBadge', 'FollowUpBlock']
    },
    {
      name: 'Data',
      components: ['Metric', 'BarChart', 'DataList']
    },
    {
      name: 'Forms',
      components: ['Form', 'FormControl', 'Input', 'Select', 'SelectItem', 'SubmitButton'],
      notes: [
        'Bind Input/Select value to $variables. Prefer Mutation + @Run for writes when tools are available.'
      ]
    },
    {
      name: 'Losi',
      components: [
        'TaskViews',
        'TaskCard',
        'NotesBrowser',
        'ContactsPanel',
        'ContactCard',
        'ApprovalCard',
        'ActionButton'
      ],
      notes: [
        'PREFER TaskViews for Space tasks — live board/list/table/timeline/calendar/gantt with drag-to-update and click-to-open modal.',
        'Use TaskCard only for a single highlighted task (pass taskId so click opens the modal).',
        'Use NotesBrowser / ContactsPanel for live notes and Nexus CRM — not static DataList invent-rows.',
        'ApprovalCard for Allow/Deny. Query()/Mutation() still fine for metrics and create forms.'
      ]
    }
  ],
  components: [
    Stack,
    Text,
    Heading,
    Metric,
    Card,
    ActionButton,
    TaskViews,
    TaskCard,
    NotesBrowser,
    ContactsPanel,
    ContactCard,
    ApprovalCard,
    BarChart,
    DataList,
    StatusBadge,
    FollowUpBlock,
    Form,
    FormControl,
    Input,
    Select,
    SelectItem,
    SubmitButton
  ]
})
